import { useState, useEffect, type ReactNode } from "react";
import { motion } from "motion/react";
import { useTheme } from "./ThemeProvider";
import { Logo } from "./Logo";

// ── Particles ────────────────────────────────────────────────────────
function Particles({ count = 20 }: { count?: number }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="absolute rounded-full pointer-events-none"
          style={{
            width: 2 + (i % 4) * 1.5,
            height: 2 + (i % 4) * 1.5,
            background: "var(--p-epic-light)",
            opacity: 0.15 + (i % 3) * 0.05,
            top: `${8 + (i * 7.3) % 85}%`,
            left: `${3 + (i * 11.7) % 92}%`,
            animation: `particleDrift ${20 + i * 2.5}s ease-in-out infinite`,
            animationDelay: `${i * -1.7}s`,
          }}
        />
      ))}
    </>
  );
}

// ── Phase pills ──────────────────────────────────────────────────────
const phases = [
  { label: "MAP", color: "var(--p-rare)", desc: "Data Intelligence" },
  { label: "EARN", color: "var(--p-epic)", desc: "Creator Activation" },
  { label: "CONVERT", color: "var(--p-legendary)", desc: "Revenue Outcome" },
];

// ── CSS animation helper ─────────────────────────────────────────────
function FadeUp({ delay, duration = 0.8, children, className = "" }: {
  delay: number; duration?: number; children: ReactNode; className?: string;
}) {
  return (
    <div
      className={className}
      style={{
        opacity: 0,
        animation: `heroFadeUp ${duration}s cubic-bezier(0.16, 1, 0.3, 1) ${delay}s forwards`,
      }}
    >
      {children}
    </div>
  );
}

function SpinIn({ delay, duration = 1, children, className = "" }: {
  delay: number; duration?: number; children: ReactNode; className?: string;
}) {
  return (
    <div
      className={className}
      style={{
        opacity: 0,
        animation: `heroSpinIn ${duration}s cubic-bezier(0.175, 0.885, 0.32, 1.1) ${delay}s forwards`,
      }}
    >
      {children}
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────
interface OpeningSequenceProps {
  children: ReactNode;
  skip?: boolean;
}

export function OpeningSequence({ children, skip = false }: OpeningSequenceProps) {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const [phase, setPhase] = useState<"intro" | "exiting" | "done">(skip ? "done" : "intro");

  // Skip if already seen this session
  useEffect(() => {
    if (skip) return;
    if (sessionStorage.getItem("exp-opening-seen")) setPhase("done");
  }, [skip]);

  // Auto-advance after 6s (enough time to enjoy the sequence)
  useEffect(() => {
    if (phase !== "intro") return;
    const timer = setTimeout(() => setPhase("exiting"), 6000);
    return () => clearTimeout(timer);
  }, [phase]);

  // Mark done after exit animation
  useEffect(() => {
    if (phase !== "exiting") return;
    const timer = setTimeout(() => {
      setPhase("done");
      sessionStorage.setItem("exp-opening-seen", "1");
    }, 1200);
    return () => clearTimeout(timer);
  }, [phase]);

  if (phase === "done") {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      >
        {children}
      </motion.div>
    );
  }

  const isExiting = phase === "exiting";

  return (
    <>
      {/* Pre-render layout behind (invisible) */}
      <div className="fixed inset-0 opacity-0 pointer-events-none">{children}</div>

      {/* Cover */}
      <div
        className="fixed inset-0 z-[9999] flex flex-col items-center justify-center overflow-hidden cursor-pointer"
        onClick={() => phase === "intro" && setPhase("exiting")}
        style={{
          background: isDark ? "var(--p-gray-900)" : "var(--p-gray-50)",
          transition: "transform 1.2s cubic-bezier(0.16, 1, 0.3, 1), opacity 1.2s cubic-bezier(0.16, 1, 0.3, 1), filter 1.2s cubic-bezier(0.16, 1, 0.3, 1)",
          ...(isExiting ? {
            transform: "scale(1.15)",
            opacity: 0,
            filter: "blur(20px) brightness(1.5)",
            pointerEvents: "none" as const,
          } : {}),
        }}
      >
        {/* Radial gradient orbs */}
        <div
          className="absolute inset-0"
          style={{
            background: isDark
              ? `radial-gradient(ellipse 90% 70% at 20% 10%, color-mix(in oklch, var(--p-rare) 16%, transparent) 0%, transparent 65%),
                 radial-gradient(ellipse 70% 60% at 75% 20%, color-mix(in oklch, var(--p-epic) 26%, transparent) 0%, transparent 60%),
                 radial-gradient(ellipse 60% 60% at 85% 88%, color-mix(in oklch, var(--p-legendary) 16%, transparent) 0%, transparent 65%)`
              : `radial-gradient(ellipse 80% 60% at 20% 10%, var(--p-gray-50) 0%, transparent 60%),
                 radial-gradient(ellipse 70% 60% at 75% 20%, var(--p-gray-50) 0%, transparent 55%),
                 radial-gradient(ellipse 60% 60% at 85% 88%, var(--p-gray-50) 0%, transparent 60%)`,
          }}
        />

        {/* Dark mode: animated gradient overlay */}
        {isDark && (
          <div
            className="absolute inset-0 opacity-40"
            style={{
              background: "linear-gradient(130deg, var(--p-gray-900), var(--p-epic-dark), var(--p-gray-900), var(--p-gray-950), var(--p-gray-900))",
              backgroundSize: "320% 320%",
              animation: "gradientShift 18s cubic-bezier(0.16, 1, 0.3, 1) infinite",
            }}
          />
        )}

        {/* Grid overlay */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            opacity: isDark ? 1 : 0.3,
            backgroundImage: isDark
              ? "linear-gradient(color-mix(in oklch, var(--p-white) 2%, transparent) 1px, transparent 1px), linear-gradient(90deg, color-mix(in oklch, var(--p-white) 2%, transparent) 1px, transparent 1px)"
              : "linear-gradient(color-mix(in oklch, var(--p-black) 3%, transparent) 1px, transparent 1px), linear-gradient(90deg, color-mix(in oklch, var(--p-black) 3%, transparent) 1px, transparent 1px)",
            backgroundSize: isDark ? "44px 44px" : "64px 64px",
          }}
        />

        {/* Light mode: purple radial glow */}
        {!isDark && (
          <div
            className="absolute rounded-full"
            style={{
              width: 800,
              height: 800,
              background: "radial-gradient(circle, color-mix(in oklch, var(--p-epic-light) 25%, transparent) 0%, transparent 70%)",
              top: "50%",
              left: "50%",
              animation: "subtleFloat 8s ease-in-out infinite",
            }}
          />
        )}

        {/* Scanline */}
        <div
          className="absolute left-0 right-0 h-px pointer-events-none"
          style={{
            background: "linear-gradient(90deg, transparent 15%, color-mix(in oklch, var(--p-rare) 40%, transparent) 34%, color-mix(in oklch, var(--p-epic) 40%, transparent) 57%, color-mix(in oklch, var(--p-legendary) 40%, transparent) 80%, transparent 95%)",
            animation: "scanline 10s linear infinite",
            opacity: isDark ? 1 : 0.5,
          }}
        />

        {/* Particles (dark mode only) */}
        {isDark && <Particles count={20} />}

        {/* ── Hero content (CSS animations) ── */}
        <div className="relative z-10 text-center">
          {/* Version tag */}
          <FadeUp delay={0.2} className={`font-mono exp-small font-semibold tracking-[0.22em] uppercase mb-6 ${isDark ? "text-[var(--p-epic-light)]" : "text-[var(--p-epic)]"}`}>
            EngageXP Design System v2
          </FadeUp>

          {/* Logomark reveal */}
          <SpinIn delay={0.5} className="flex justify-center mb-6">
            <Logo
              variant="logomark"
              colorScheme={isDark ? "white" : "brand"}
              size={100}
            />
          </SpinIn>

          {/* EngageXP logo (mark + text) */}
          <FadeUp delay={0.9} className="flex justify-center mb-8">
            <Logo
              variant="horizontal"
              colorScheme={isDark ? "white" : "brand"}
              size={360}
            />
          </FadeUp>

          {/* Hero headline */}
          <FadeUp delay={1.2} duration={1.1} className="mb-8 leading-none">
            <div
              className={`font-['Space_Grotesk'] font-black tracking-tight uppercase ${isDark ? "text-white" : "text-[var(--p-gray-800)]"}`}
              style={{ fontSize: "clamp(36px, 6vw, 72px)", letterSpacing: "-0.03em" }}
            >
              TURN THE ORDINARY
            </div>
            <div
              className="font-['Space_Grotesk'] font-black tracking-tight uppercase"
              style={{ fontSize: "clamp(36px, 6vw, 72px)", letterSpacing: "-0.03em" }}
            >
              <span className={isDark ? "text-white" : "text-[var(--p-gray-800)]"}>INTO </span>
              <span
                style={{
                  color: "var(--p-legendary)",
                  textShadow: isDark
                    ? "0 0 40px color-mix(in oklch, var(--p-legendary) 55%, transparent), 0 0 80px color-mix(in oklch, var(--p-legendary) 28%, transparent)"
                    : "0 0 20px color-mix(in oklch, var(--p-legendary) 28%, transparent)",
                  animation: isDark ? "legendaryPulse 3s ease-in-out infinite" : "none",
                }}
              >
                LEGENDARY
              </span>
            </div>
          </FadeUp>

          {/* MAP → EARN → CONVERT pills */}
          <div className="flex gap-3 justify-center mb-10 flex-wrap">
            {phases.map((p, i) => (
              <FadeUp key={p.label} delay={2.2 + i * 0.15} duration={0.5}>
                <span
                  className="inline-block font-mono exp-small font-bold tracking-wider px-4 py-2.5 rounded-full"
                  style={{
                    color: p.color,
                    background: `${p.color}${isDark ? "16" : "12"}`,
                    border: `1px solid ${p.color}${isDark ? "55" : "40"}`,
                    boxShadow: isDark ? `0 0 18px ${p.color}22` : "none",
                  }}
                >
                  {p.label} · {p.desc}
                </span>
              </FadeUp>
            ))}
          </div>

          {/* Footer info */}
          <FadeUp delay={2.8} duration={0.6} className={`font-mono exp-small ${isDark ? "text-muted-foreground" : "text-muted-foreground"}`}>
            v2.0 · March 2026 · EngageXP Creative Direction
          </FadeUp>

          {/* Click hint */}
          <div
            className={`mt-8 font-mono exp-caption tracking-widest uppercase ${isDark ? "text-muted-foreground" : "text-muted-foreground"}`}
            style={{
              opacity: 0,
              animation: "heroPulse 2s ease-in-out 3.5s infinite",
            }}
          >
            Click anywhere to enter
          </div>
        </div>

        {/* Bottom gradient fade */}
        <div
          className="absolute bottom-0 left-0 right-0 h-36 pointer-events-none"
          style={{
            background: `linear-gradient(transparent, ${isDark ? "var(--p-gray-900)" : "var(--p-gray-50)"})`,
          }}
        />
      </div>
    </>
  );
}
